import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { fmtDate, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading, EmptyState } from "@/components/Shared";
import { MessageCircle, Send, ArrowLeft, Paperclip, Loader2, X } from "lucide-react";
import { toast } from "sonner";

export default function Messages() {
  const { tid } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [threads, setThreads] = useState(null);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [uploading, setUploading] = useState(false);
  const [attachBlob, setAttachBlob] = useState(null); // {file, preview}
  const [previewImg, setPreviewImg] = useState(null);
  const fileRef = useRef(null);
  const scrollerRef = useRef(null);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  const loadThreads = () => api.get("/messages/threads").then((r) => setThreads(r.data)).catch(() => setThreads([]));

  useEffect(() => { loadThreads(); }, []);

  useEffect(() => {
    if (!tid) { setActive(null); setMessages([]); return; }
    api.get(`/messages/threads/${tid}`).then((r) => {
      setActive(r.data.thread);
      setMessages(r.data.messages || []);
      loadThreads();
    }).catch(() => toast.error("Discussion introuvable"));
  }, [tid]);

  useEffect(() => {
    if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [messages]);

  const send = async () => {
    if (!draft.trim() && !attachBlob) return;
    try {
      if (attachBlob) {
        setUploading(true);
        const fd = new FormData();
        fd.append("file", attachBlob.file);
        fd.append("caption", draft.trim());
        const r = await api.post(`/messages/threads/${tid}/attachments`, fd);
        setMessages((prev) => [...prev, r.data]);
        setAttachBlob(null);
        setDraft("");
      } else {
        const r = await api.post(`/messages/threads/${tid}`, { body: draft.trim() });
        setMessages((prev) => [...prev, r.data]);
        setDraft("");
      }
      loadThreads();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const pickFile = (f) => {
    if (!f) return;
    if (!/^image\//.test(f.type)) { toast.error("Image uniquement (JPG, PNG, WEBP)"); return; }
    if (f.size > 5 * 1024 * 1024) { toast.error("Image trop volumineuse (max 5 Mo)"); return; }
    const url = URL.createObjectURL(f);
    setAttachBlob({ file: f, preview: url });
  };

  const imgSrc = (m) => m.attachment_url?.startsWith("/api/")
    ? `${BACKEND}${m.attachment_url}`
    : m.attachment_url;

  if (threads === null) return <Loading />;

  return (
    <div>
      <PageHeader title="Messagerie" subtitle="Discussions privées avec les pharmaciens du réseau." />
      <div className="grid md:grid-cols-[320px_1fr] gap-4 bg-white border border-slate-200 rounded-xl overflow-hidden min-h-[520px]">
        {/* Threads list */}
        <aside className={`border-r border-slate-200 ${tid ? "hidden md:block" : ""}`}>
          <div className="px-4 py-3 border-b border-slate-100 text-xs font-bold text-slate-500 uppercase tracking-wide">Conversations</div>
          {threads.length === 0 ? (
            <div className="p-6"><EmptyState title="Aucune discussion" subtitle="Lancez un échange depuis un surstock ou une demande." icon={MessageCircle} /></div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {threads.map((t) => {
                const isActive = t.id === tid;
                return (
                  <li key={t.id}>
                    <button
                      onClick={() => navigate(`/messages/${t.id}`)}
                      data-testid={`thread-${t.id}`}
                      className={`w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors duration-200 ${isActive ? "bg-indigo-50" : ""}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-bold text-slate-900 text-sm truncate">{t.other_user_name || "Pharmacien"}</span>
                        {t.unread_me > 0 && <span className="text-[10px] font-bold bg-indigo-600 text-white rounded-full h-5 min-w-5 px-1 grid place-items-center" data-testid={`thread-unread-${t.id}`}>{t.unread_me}</span>}
                      </div>
                      {t.context?.product_name && <div className="text-xs text-indigo-600 font-semibold mt-0.5 truncate">{t.context.product_name}</div>}
                      <div className="text-xs text-slate-400 truncate mt-0.5">{t.last_message || "Nouvelle discussion"}</div>
                      <div className="text-[10px] text-slate-400 mt-0.5">{fmtDate(t.last_message_at)}</div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </aside>

        {/* Conversation */}
        <section className={`flex flex-col ${!tid ? "hidden md:flex" : ""}`}>
          {!tid ? (
            <div className="flex-1 grid place-items-center p-6">
              <div className="text-center text-slate-400 text-sm">Sélectionnez une conversation à gauche.</div>
            </div>
          ) : active ? (
            <>
              <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-3">
                <button onClick={() => navigate("/messages")} className="md:hidden text-slate-500" data-testid="back-to-threads"><ArrowLeft className="h-4 w-4" /></button>
                <div className="flex-1">
                  <div className="font-bold text-slate-900">{active.other_user_name}</div>
                  {active.context?.product_name && <div className="text-xs text-indigo-600 font-semibold">À propos de : {active.context.product_name}</div>}
                </div>
              </div>
              <div ref={scrollerRef} className="flex-1 overflow-y-auto p-5 space-y-3 bg-slate-50 min-h-[320px] max-h-[60vh]" data-testid="messages-scroller">
                {messages.length === 0 ? (
                  <div className="text-center text-sm text-slate-400 py-10">Démarrez la conversation</div>
                ) : messages.map((m) => {
                  const mine = m.sender_id === user?.id;
                  const bubbleBg = mine ? "bg-indigo-600 text-white rounded-br-sm" : "bg-white border border-slate-200 text-slate-900 rounded-bl-sm";
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
                      <div className={`max-w-[75%] rounded-2xl px-4 py-2 text-sm shadow-sm ${bubbleBg}`}>
                        {m.attachment_url && (
                          <button type="button" onClick={() => setPreviewImg(imgSrc(m))} data-testid={`msg-img-${m.id}`} className="block mb-1.5 rounded-lg overflow-hidden">
                            <img src={imgSrc(m)} alt={m.attachment_name || "pièce jointe"} className="max-h-72 max-w-full object-contain bg-slate-100 rounded-lg" />
                          </button>
                        )}
                        {m.body && <div className="whitespace-pre-wrap break-words">{m.body}</div>}
                        <div className={`text-[10px] mt-1 ${mine ? "text-indigo-100" : "text-slate-400"}`}>{fmtDate(m.created_at)}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {attachBlob && (
                <div className="px-3 pt-3 border-t border-slate-100 flex items-center gap-2" data-testid="attach-preview">
                  <img src={attachBlob.preview} alt="aperçu" className="h-14 w-14 object-cover rounded-lg border border-slate-200" />
                  <div className="text-xs text-slate-500 flex-1 truncate">{attachBlob.file.name}</div>
                  <button onClick={() => setAttachBlob(null)} className="text-slate-400 hover:text-rose-500" data-testid="attach-clear"><X className="h-4 w-4" /></button>
                </div>
              )}
              <div className="p-3 border-t border-slate-100 flex items-center gap-2">
                <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="attach-input"
                  onChange={(e) => { pickFile(e.target.files?.[0]); e.target.value = ""; }} />
                <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="attach-btn"
                  className="h-11 w-11 grid place-items-center rounded-lg bg-white border border-slate-200 text-slate-500 hover:bg-slate-50 transition-colors duration-200 disabled:opacity-50">
                  <Paperclip className="h-5 w-5" />
                </button>
                <input
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())}
                  placeholder={attachBlob ? "Ajouter une légende (facultatif)…" : "Écrire un message..."}
                  data-testid="message-input"
                  className="flex-1 h-11 px-4 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-600 text-sm"
                />
                <button onClick={send} disabled={uploading || (!draft.trim() && !attachBlob)} data-testid="send-message" className="h-11 px-4 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-sm inline-flex items-center gap-2 transition-colors duration-200 disabled:opacity-50">
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4" /> Envoyer</>}
                </button>
              </div>
            </>
          ) : (
            <div className="flex-1 grid place-items-center"><Loading /></div>
          )}
        </section>
      </div>
      {previewImg && (
        <div className="fixed inset-0 z-50 bg-black/80 grid place-items-center p-4" onClick={() => setPreviewImg(null)} data-testid="img-preview-modal">
          <img src={previewImg} alt="aperçu" className="max-h-[90vh] max-w-full object-contain" />
          <button onClick={() => setPreviewImg(null)} className="absolute top-6 right-6 text-white"><X className="h-6 w-6" /></button>
        </div>
      )}
    </div>
  );
}
